/**
 * Gefuehrter Datenpflege-Wizard — CODA-505
 *
 * Design-Lenses angewendet:
 * - Goal-Gradient: Fortschrittsbalken + „X von Y GPs bereinigt"
 * - Progressive Disclosure: Aktions-Panel je nach RadioGroup-Auswahl
 * - Recognition over Recall: alle 4 Aktionsoptionen stets sichtbar
 * - Zeigarnik: Abschluss-Seite schliesst den offenen Loop
 * - Forgiveness: Exit-Bestaetigungs-Modal
 * - Peak-End Rule: positive Abschluss-Seite
 * - Miller's Law: max. 4 Issues, 4 Aktionsoptionen
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ErrorState,
  IconButton,
  LoadingState,
  Modal,
  PageHeader,
  RadioGroup,
  Select,
  StatusBadge,
} from '@/components/ui'
import { apiClient } from '@/lib/api.ts'

// ---------- Typen ----------

interface WizardGp {
  id: string
  name: string
  bpType: 'customer' | 'supplier' | 'prospect'
  qualityScore: number
  issues: Array<{ code: string; message: string; severity: 'error' | 'warning' }>
  contactEmail: string | null
}

interface WizardSuggestion {
  id: string
  field: string
  label: string
  suggestedValue: string
  source: string
  confidence: number
}

interface QueueResponse {
  gp: WizardGp | null
  suggestions: WizardSuggestion[]
  remaining: number
  total: number
}

interface Campaign {
  id: string
  name: string
  status: string
}

// ---------- Hilfsfunktionen ----------

const TYPE_LABEL: Record<string, string> = {
  customer: 'Kunde',
  supplier: 'Lieferant',
  prospect: 'Interessent',
}
const TYPE_STATUS: Record<string, string> = {
  customer: 'active',
  supplier: 'info',
  prospect: 'pending',
}

function QualityBar({ score }: { score: number }) {
  const color =
    score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-amber-400' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-small text-gray-600 tabular-nums w-10 text-right">
        {score} %
      </span>
    </div>
  )
}

function ConfidencePill({ value }: { value: number }) {
  const pct = Math.round(value * 100)
  const variant =
    pct >= 85 ? 'success' : pct >= 65 ? 'warning' : ('danger' as const)
  return <Badge variant={variant}>{pct} % Konfidenz</Badge>
}

// ---------- Aktions-Optionen fuer RadioGroup ----------

type ActionType = 'ai' | 'campaign' | 'manual' | 'skip'

const ACTION_OPTIONS: { value: ActionType; label: string; hint: string }[] = [
  {
    value: 'ai',
    label: 'KI-Vorschläge reviewen',
    hint: 'Automatisch gefundene Daten mit Quellenangabe und Konfidenz prüfen',
  },
  {
    value: 'campaign',
    label: 'Kampagnen-Formular senden',
    hint: 'E-Mail an GP-Kontakt mit Selbstauskunfts-Link senden',
  },
  {
    value: 'manual',
    label: 'Manuell bearbeiten',
    hint: 'GP-Detailseite öffnen und Daten selbst pflegen',
  },
  {
    value: 'skip',
    label: 'Überspringen',
    hint: 'Diesen GP vorübergehend zurückstellen',
  },
]

const SKIP_OPTIONS = [
  { value: '30', label: '30 Tage' },
  { value: '60', label: '60 Tage' },
  { value: '90', label: '90 Tage' },
]

// ---------- Haupt-Komponente ----------

export default function GpDatenpflegeWizardPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Scope: ?ids=id1,id2 oder ?id=singleId (Legacy von Uebersicht)
  const idsParam = searchParams.get('ids') ?? (searchParams.get('id') ? searchParams.get('id')! : null)
  const sessionIds = idsParam ?? undefined
  // Pre-select action via ?action=campaign|ai|skip|manual
  const actionParam = searchParams.get('action') as ActionType | null

  // Queue-State
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [currentGp, setCurrentGp] = useState<WizardGp | null>(null)
  const [suggestions, setSuggestions] = useState<WizardSuggestion[]>([])
  const [remaining, setRemaining] = useState(0)
  const [total, setTotal] = useState(0)
  const [doneCount, setDoneCount] = useState(0)
  const [showDone, setShowDone] = useState(false)
  const [lastProcessedId, setLastProcessedId] = useState<string | undefined>(undefined)

  // Aktion-State
  const [action, setAction] = useState<ActionType>(actionParam ?? 'ai')
  const [skipDays, setSkipDays] = useState('30')
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set())
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Kampagnen-State
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('')
  const [campaignSendDone, setCampaignSendDone] = useState(false)

  // Exit-Modal
  const [showExitConfirm, setShowExitConfirm] = useState(false)

  // Statistik fuer Abschluss-Seite
  const [stats, setStats] = useState({ applied: 0, campaigns: 0, skipped: 0 })

  // ---------- Daten laden ----------

  const fetchNext = useCallback(async (after?: string) => {
    setLoading(true)
    setLoadError(null)
    try {
      const params = new URLSearchParams()
      if (sessionIds) params.set('ids', sessionIds)
      if (after) params.set('after', after)
      const { data } = await apiClient.get<QueueResponse>(
        `/business-partners/datenpflege-queue?${params.toString()}`,
      )
      if (!data.gp) {
        setShowDone(true)
      } else {
        setCurrentGp(data.gp)
        setSuggestions(data.suggestions)
        setRemaining(data.remaining)
        setTotal(data.total)
        setAction('ai')
        setAcceptedIds(new Set())
        setRejectedIds(new Set())
        setSubmitError(null)
        setCampaignSendDone(false)
        setSelectedCampaignId('')
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler beim Laden der Queue'
      setLoadError(msg)
    } finally {
      setLoading(false)
    }
  }, [sessionIds])

  useEffect(() => {
    fetchNext()
  }, [fetchNext])

  // Kampagnen laden wenn Nutzer „campaign" waehlt
  useEffect(() => {
    if (action === 'campaign' && campaigns === null) {
      apiClient.get<Campaign[]>('/gp-campaigns').then((r) => {
        setCampaigns(r.data.filter((c) => c.status !== 'archived'))
      }).catch(() => setCampaigns([]))
    }
  }, [action, campaigns])

  // ---------- Aktionen ----------

  async function submitAndNext() {
    if (!currentGp) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      if (action === 'ai') {
        const ids = [...acceptedIds]
        if (ids.length > 0) {
          await apiClient.post(`/business-partners/${currentGp.id}/datenpflege-action`, {
            action: 'apply_suggestion',
            suggestionIds: ids,
          })
          setStats((s) => ({ ...s, applied: s.applied + ids.length }))
        }
      } else if (action === 'skip') {
        await apiClient.post(`/business-partners/${currentGp.id}/datenpflege-action`, {
          action: 'skip',
          skipDays: Number(skipDays),
        })
        setStats((s) => ({ ...s, skipped: s.skipped + 1 }))
      } else if (action === 'campaign') {
        if (selectedCampaignId && !campaignSendDone) {
          await apiClient.post(`/business-partners/${currentGp.id}/datenpflege-action`, {
            action: 'send_campaign',
            campaignId: selectedCampaignId,
          })
          setCampaignSendDone(true)
          setStats((s) => ({ ...s, campaigns: s.campaigns + 1 }))
        }
      }
      // manual: no server action, just navigate forward
      const processedId = currentGp.id
      setLastProcessedId(processedId)
      setDoneCount((n) => n + 1)
      await fetchNext(processedId)
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler beim Ausführen der Aktion'
      setSubmitError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  function acceptSuggestion(id: string) {
    setAcceptedIds((prev) => { const n = new Set(prev); n.add(id); return n })
    setRejectedIds((prev) => { const n = new Set(prev); n.delete(id); return n })
  }

  function rejectSuggestion(id: string) {
    setRejectedIds((prev) => { const n = new Set(prev); n.add(id); return n })
    setAcceptedIds((prev) => { const n = new Set(prev); n.delete(id); return n })
  }

  // ---------- Abschluss-Seite ----------

  if (showDone) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <PageHeader
          title="Datenpflege abgeschlossen"
          breadcrumbs={[
            { label: 'Geschäftspartner', to: '/gp' },
            { label: 'Datenpflege', to: '/gp/datenpflege' },
            { label: 'Wizard' },
          ]}
        />
        <Card>
          <Card.Body className="flex flex-col items-center gap-6 py-16">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-h3 font-semibold text-gray-900">
                {doneCount} von {total || doneCount} GPs bearbeitet
              </p>
              <p className="text-body text-gray-500">
                KI-Vorschläge angenommen: {stats.applied} &middot; Kampagnen
                versendet: {stats.campaigns} &middot; Übersprungen:{' '}
                {stats.skipped}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="primary"
                onClick={() => navigate('/gp/datenpflege')}
              >
                Zurück zur Übersicht
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setDoneCount(0)
                  setShowDone(false)
                  setStats({ applied: 0, campaigns: 0, skipped: 0 })
                  setLastProcessedId(undefined)
                  fetchNext()
                }}
              >
                Nochmal starten
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  // ---------- Lade- und Fehlerzustaende ----------

  if (loading && !currentGp) {
    return (
      <div className="max-w-3xl mx-auto">
        <LoadingState label="Datenpflege-Queue wird geladen…" />
      </div>
    )
  }

  if (loadError && !currentGp) {
    return (
      <div className="max-w-3xl mx-auto">
        <ErrorState
          title="Fehler beim Laden"
          description={loadError}
          onRetry={() => { fetchNext(lastProcessedId) }}
          retryLabel="Erneut versuchen"
        />
      </div>
    )
  }

  if (!currentGp) return null

  // ---------- Wizard-Seite ----------

  const progressPct = total > 0 ? Math.round((doneCount / total) * 100) : 0

  return (
    <>
      <div className="space-y-4 max-w-3xl mx-auto">
        {/* Kopfzeile mit Exit-Button */}
        <div className="flex items-start justify-between gap-4">
          <PageHeader
            title="Geführte Datenpflege"
            description={`GP ${doneCount + 1} von ${total}`}
            breadcrumbs={[
              { label: 'Geschäftspartner', to: '/gp' },
              { label: 'Datenpflege', to: '/gp/datenpflege' },
              { label: 'Wizard' },
            ]}
          />
          <div className="pt-1 shrink-0">
            <IconButton
              icon={<X className="w-4 h-4" />}
              aria-label="Wizard beenden"
              onClick={() => setShowExitConfirm(true)}
            />
          </div>
        </div>

        {/* Fortschrittsbalken — Goal-Gradient */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-small text-gray-500">
              {doneCount} von {total} GPs bereinigt
            </span>
            <span className="text-small text-gray-400 tabular-nums">
              {progressPct} %
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-700 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* GP-Card — Miller's Law: max. 4 Issues sichtbar */}
        <Card>
          <Card.Header>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Card.Title>{currentGp.name}</Card.Title>
                <div className="flex items-center gap-2 mt-1.5">
                  <StatusBadge
                    status={TYPE_STATUS[currentGp.bpType] ?? 'default'}
                    label={TYPE_LABEL[currentGp.bpType] ?? currentGp.bpType}
                  />
                  {currentGp.contactEmail ? (
                    <span className="text-caption text-gray-400">
                      {currentGp.contactEmail}
                    </span>
                  ) : (
                    <Badge variant="warning">Kein E-Mail-Kontakt</Badge>
                  )}
                </div>
              </div>
              <div className="w-44 shrink-0">
                <p className="text-caption text-gray-400 mb-1">
                  Qualitäts-Score
                </p>
                <QualityBar score={currentGp.qualityScore} />
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <p className="text-small font-medium text-gray-600 mb-2">
              Offene Datenlücken
            </p>
            <div className="space-y-2">
              {currentGp.issues.slice(0, 4).map((issue) => (
                <div key={issue.code} className="flex items-center gap-2.5">
                  <div
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      issue.severity === 'error' ? 'bg-red-500' : 'bg-amber-400'
                    }`}
                  />
                  <span className="text-body text-gray-800">
                    {issue.message}
                  </span>
                  <Badge
                    variant={issue.severity === 'error' ? 'danger' : 'warning'}
                    size="sm"
                  >
                    {issue.severity === 'error' ? 'Pflichtfeld' : 'Empfohlen'}
                  </Badge>
                </div>
              ))}
              {currentGp.issues.length > 4 && (
                <span className="text-caption text-gray-400">
                  +{currentGp.issues.length - 4} weitere …
                </span>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Aktions-Auswahl — Recognition over Recall */}
        <Card>
          <Card.Header>
            <Card.Title>Wie möchten Sie vorgehen?</Card.Title>
          </Card.Header>
          <Card.Body>
            <RadioGroup
              options={ACTION_OPTIONS}
              value={action}
              onValueChange={(v) => {
                setAction(v as ActionType)
                setSubmitError(null)
                setCampaignSendDone(false)
              }}
              orientation="vertical"
            />
          </Card.Body>
        </Card>

        {/* KI-Vorschlaege — Progressive Disclosure */}
        {action === 'ai' && (
          <Card>
            <Card.Header>
              <Card.Title>
                KI-Vorschläge{' '}
                {suggestions.length > 0 && (
                  <Badge variant="info" className="ml-2">
                    {suggestions.length}
                  </Badge>
                )}
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {suggestions.length === 0 ? (
                <p className="text-body text-gray-500">
                  Keine KI-Vorschläge für diesen GP vorhanden. Sie können
                  direkt mit „Weiter" fortfahren.
                </p>
              ) : (
                <div className="space-y-3">
                  {suggestions.map((s) => {
                    const accepted = acceptedIds.has(s.id)
                    const rejected = rejectedIds.has(s.id)
                    return (
                      <div
                        key={s.id}
                        className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                          accepted
                            ? 'border-green-200 bg-green-50'
                            : rejected
                              ? 'border-gray-100 bg-gray-50 opacity-50'
                              : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-small font-medium text-gray-600">
                            {s.label}
                          </p>
                          <p className="text-body font-mono text-gray-900 mt-0.5 truncate">
                            {s.suggestedValue}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-caption text-gray-400">
                              Quelle: {s.source}
                            </span>
                            <ConfidencePill value={s.confidence} />
                          </div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <Button
                            variant={accepted ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => acceptSuggestion(s.id)}
                          >
                            <ThumbsUp className="w-3.5 h-3.5 mr-1" />
                            Annehmen
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => rejectSuggestion(s.id)}
                          >
                            <ThumbsDown className="w-3.5 h-3.5 mr-1" />
                            Ablehnen
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Kampagnen-Formular */}
        {action === 'campaign' && (
          <Card>
            <Card.Header>
              <Card.Title>Kampagnen-Formular senden</Card.Title>
            </Card.Header>
            <Card.Body className="space-y-3">
              {!currentGp.contactEmail ? (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                  <Badge variant="warning">Kein E-Mail-Kontakt</Badge>
                  <p className="text-body text-amber-800">
                    Für diesen GP ist keine E-Mail-Adresse hinterlegt. Bitte
                    zuerst manuell ergänzen.
                  </p>
                </div>
              ) : campaignSendDone ? (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-green-200 bg-green-50">
                  <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                  <p className="text-body text-green-800">
                    Kampagnen-E-Mail erfolgreich gesendet an{' '}
                    {currentGp.contactEmail}. Mit „Weiter" zum nächsten GP.
                  </p>
                </div>
              ) : (
                <>
                  {campaigns === null ? (
                    <p className="text-body text-gray-400">
                      Kampagnen werden geladen…
                    </p>
                  ) : campaigns.length === 0 ? (
                    <p className="text-body text-gray-500">
                      Keine aktiven Kampagnen vorhanden. Bitte zuerst eine
                      Kampagne in der{' '}
                      <a
                        href="/gp/kampagnen"
                        className="text-brand-700 underline"
                      >
                        Kampagnen-Übersicht
                      </a>{' '}
                      anlegen.
                    </p>
                  ) : (
                    <>
                      <p className="text-small text-gray-600">
                        Kampagne auswählen und Formular-Link an{' '}
                        <strong>{currentGp.contactEmail}</strong> senden:
                      </p>
                      <Select
                        options={[
                          { value: '', label: '— Kampagne wählen —' },
                          ...campaigns.map((c) => ({
                            value: c.id,
                            label: c.name,
                          })),
                        ]}
                        value={selectedCampaignId}
                        onValueChange={setSelectedCampaignId}
                      />
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Manuell bearbeiten */}
        {action === 'manual' && (
          <Card>
            <Card.Body className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-body text-gray-800 font-medium">
                  GP-Detailseite öffnen
                </p>
                <p className="text-small text-gray-400 mt-0.5">
                  Öffnet in neuem Tab — Wizard bleibt geöffnet
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  window.open(`/gp/${currentGp.id}`, '_blank', 'noopener')
                }
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                GP öffnen
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Ueberspringen */}
        {action === 'skip' && (
          <Card>
            <Card.Body className="flex items-center gap-4">
              <Clock className="w-5 h-5 text-gray-400 shrink-0" />
              <p className="text-body text-gray-700 flex-1">
                GP zurückstellen für:
              </p>
              <Select
                options={SKIP_OPTIONS}
                value={skipDays}
                onValueChange={setSkipDays}
              />
            </Card.Body>
          </Card>
        )}

        {/* Fehleranzeige */}
        {submitError && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50">
            <p className="text-body text-red-800">{submitError}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pb-8 pt-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/gp/datenpflege')}
            disabled={submitting}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Zurück zur Übersicht
          </Button>
          <Button
            variant="primary"
            onClick={submitAndNext}
            disabled={
              submitting ||
              loading ||
              (action === 'campaign' &&
                !!currentGp.contactEmail &&
                !selectedCampaignId &&
                !campaignSendDone)
            }
          >
            {submitting ? (
              'Wird gespeichert…'
            ) : remaining === 0 ? (
              'Abschließen'
            ) : (
              <>
                Weiter
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Exit-Bestaetigungs-Modal — Forgiveness */}
      <Modal
        open={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        title="Wizard beenden?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-body text-gray-700">
            Fortschritt ({doneCount} von {total} GPs) wurde gespeichert. Sie
            können den Wizard jederzeit fortsetzen.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setShowExitConfirm(false)}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/gp/datenpflege')}
            >
              Beenden
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
